/**
 * MobileAddTeamsMembersModal
 *
 * MUI full-screen Dialog replacement for AddTeamsMembersModal (PrimeReact Dialog).
 * Mobile-first design with glassmorphic styling, sticky AppBar and bottom button.
 *
 * Components from MUI: Dialog, AppBar, Toolbar, IconButton, Typography, Autocomplete,
 *   TextField, Button, Box, Slide
 */
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";

// MUI Components
import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Autocomplete,
  TextField,
  Button,
  Box,
  Slide,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonAddIcon from "@mui/icons-material/PersonAdd";

// Redux
import { getCollabsNames } from "../../../components/vcomp/vcompSlice";
import {
  addCollabMission,
  editCollabMission,
  getCollabMissions,
} from "../../../components/teams/teamsSlice";
import { getTeamCollabs } from "../../../components/clients/clientsSlice";
import { getPostes } from "../../../components/collabs/collabsSlice";

// Shared utilities
import Langs from "../../../app/lang/langs";
import { showErrorToast } from "../../../app/utils/helpers";

/* ------------------------------------------------------------------ */
/*  Shared style constants                                             */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)";
const BORDER_RADIUS_CARD = "16px";
const BORDER_RADIUS_INPUT = "12px";
const BORDER_RADIUS_BUTTON = "14px";

const fieldLabelSx = {
  fontWeight: 600,
  mb: 0.75,
  letterSpacing: "-0.01em",
  color: "text.secondary",
  fontSize: "0.82rem",
  textTransform: "uppercase",
};

const glassmorphicTextFieldSx = {
  mb: 3,
  "& .MuiOutlinedInput-root": {
    borderRadius: BORDER_RADIUS_INPUT,
    backgroundColor: "rgba(255,255,255,0.06)",
    backdropFilter: "blur(6px)",
    transition: TRANSITION_SMOOTH,
    "& fieldset": {
      borderColor: "rgba(0,0,0,0.08)",
      transition: TRANSITION_SMOOTH,
    },
    "&:hover fieldset": {
      borderColor: "rgba(0,0,0,0.18)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "rgba(255,144,21,0.5)",
      boxShadow: "0 0 0 3px rgba(255,144,21,0.12)",
    },
    "&.Mui-focused": {
      backgroundColor: "rgba(255,255,255,0.10)",
    },
  },
  "& input": {
    fontSize: "16px",
    transition: TRANSITION_SMOOTH,
  },
  "& input::placeholder": {
    opacity: 0.45,
  },
};

/* ------------------------------------------------------------------ */
/*  Slide transition                                                    */
/* ------------------------------------------------------------------ */
const SlideUp = React.forwardRef(function SlideUp(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/* ------------------------------------------------------------------ */
/*  Component                                                           */
/* ------------------------------------------------------------------ */
export default function MobileAddTeamsMembersModal(props) {
  const dispatch = useDispatch();

  // Redux state
  const collabsNames = useSelector((state) => state.vcomp.collabsNames);
  const postes = useSelector((state) => state.collabs.postes);
  const collabMissions = useSelector((state) => state.teams.collabMissions);

  // Local state
  const [filteredCollabs, setFilteredCollabs] = useState([]);
  const [associatedMembers, setAssociatedMembers] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [onGoingMission, setOnGoingMission] = useState(null);
  const [newPoste, setNewPoste] = useState(null);
  const [postesOptions, setPostesOptions] = useState([]);

  /* ---- Effects ---- */

  useEffect(() => {
    const options = postes?.map((poste) => ({
      label: poste.name,
      value: poste.id,
    }));
    setPostesOptions(options || []);
  }, [postes]);

  useEffect(() => {
    if (associatedMembers && associatedMembers.id) {
      dispatch(getCollabMissions({ id_collab: associatedMembers.id }));
    }
  }, [associatedMembers]);

  useEffect(() => {
    if (collabMissions && collabMissions.ongoingMissions) {
      setOnGoingMission(collabMissions.ongoingMissions[0] || null);
    }
  }, [collabMissions]);

  useEffect(() => {
    dispatch(getCollabsNames());
    dispatch(getPostes());
    setFilteredCollabs(
      collabsNames
        .filter((collab) => !props.prevCollabs.includes(collab.id))
        .map(({ id, nom, prenom }) => ({
          id,
          fullName: `${nom} ${prenom}`,
        }))
    );
  }, [props.idTeam, props.prevCollabs]);

  /* ---- Handlers ---- */

  const handleClose = () => {
    props.setVisible(false);
    setAssociatedMembers(null);
    setFilteredCollabs([]);
    setStartDate("");
    setEndDate("");
    setNewPoste(null);
  };

  const handleAddMembers = () => {
    if (associatedMembers && associatedMembers.id && props.idTeam) {
      if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
        showErrorToast(
          "La date de début doit être antérieure ou égale à la date de fin."
        );
        return;
      }
      if (!newPoste) {
        showErrorToast("Veuillez choisir un poste.");
        return;
      }

      dispatch(
        addCollabMission({
          id_team: props.idTeam,
          id_collab: associatedMembers.id,
          start_date: moment(startDate).format("YYYY-MM-DD 00:00:00"),
          end_date: endDate
            ? moment(endDate).format("YYYY-MM-DD 23:59:59")
            : null,
          id_poste: newPoste.value,
        })
      )
        .then((actionResponse) => {
          if (addCollabMission.fulfilled.match(actionResponse)) {
            if (
              actionResponse.payload.responseData &&
              actionResponse.payload.responseData.status
            ) {
              dispatch(getTeamCollabs({ id_team: props.idTeam }));
              handleClose();
            } else {
              if (
                actionResponse.payload.responseData.error &&
                actionResponse.payload.responseData.error.includes("conflict")
              ) {
                showErrorToast("La mission est en conflit avec une autre");
              }
            }
          }
        })
        .catch((error) => {
          console.error(error);
          console.error("Échec de l'ajout/suppression des membres");
        });
    }
  };

  const isFormValid =
    associatedMembers && associatedMembers.id && newPoste && startDate;

  /* ---- Render ---- */

  return (
    <Dialog
      fullScreen
      open={!!props.visible}
      onClose={handleClose}
      TransitionComponent={SlideUp}
      sx={{ zIndex: 1400 }}
    >
      {/* ---- Sticky AppBar ---- */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: "rgba(255,255,255,0.82)",
          color: "text.primary",
          boxShadow: "0 1px 12px rgba(0,0,0,0.06)",
          backdropFilter: "blur(14px)",
        }}
      >
        <Toolbar sx={{ minHeight: 56 }}>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleClose}
            aria-label="close"
            sx={{ mr: 1 }}
          >
            <CloseIcon />
          </IconButton>
          <Typography
            variant="h6"
            sx={{ flex: 1, fontWeight: 700, fontSize: "1.1rem" }}
          >
            <Langs str="ADD_MEMBERS" />
          </Typography>
        </Toolbar>
      </AppBar>

      {/* ---- Body ---- */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          px: 2,
          pt: 3,
          pb: "120px",
          bgcolor: "#f5f5f7",
        }}
      >
        <Box
          sx={{
            borderRadius: BORDER_RADIUS_CARD,
            bgcolor: "rgba(255,255,255,0.72)",
            backdropFilter: "blur(18px)",
            boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
            p: 2.5,
          }}
        >
          {/* ---- Member Selection ---- */}
          <Typography sx={fieldLabelSx}>
            <Langs str="Associated Members" />
          </Typography>
          <Autocomplete
            value={associatedMembers}
            onChange={(_, newValue) => setAssociatedMembers(newValue)}
            options={filteredCollabs}
            getOptionLabel={(option) => option.fullName || ""}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Sélectionner un collaborateur..."
                sx={glassmorphicTextFieldSx}
              />
            )}
            componentsProps={{
              popper: { sx: { zIndex: 1500 } },
            }}
            noOptionsText="Aucun résultat"
          />

          {/* ---- Poste Selection ---- */}
          <Typography sx={fieldLabelSx}>
            <Langs str="POSTE" />
          </Typography>
          <Autocomplete
            value={newPoste}
            onChange={(_, newValue) => setNewPoste(newValue)}
            options={postesOptions}
            getOptionLabel={(option) => option.label || ""}
            isOptionEqualToValue={(option, value) =>
              option.value === value?.value
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Nom du poste"
                sx={glassmorphicTextFieldSx}
              />
            )}
            componentsProps={{
              popper: { sx: { zIndex: 1500 } },
            }}
            noOptionsText="Aucun résultat"
          />

          {/* ---- Start Date ---- */}
          <Typography sx={fieldLabelSx}>
            <Langs str="START_DATE" />
          </Typography>
          <TextField
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            fullWidth
            sx={glassmorphicTextFieldSx}
            InputLabelProps={{ shrink: true }}
          />

          {/* ---- End Date ---- */}
          <Typography sx={fieldLabelSx}>
            <Langs str="END_DATE" />
          </Typography>
          <TextField
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            fullWidth
            sx={glassmorphicTextFieldSx}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              min: startDate || undefined,
            }}
            placeholder="(optionnel)"
          />
        </Box>
      </Box>

      {/* ---- Sticky Bottom Button ---- */}
      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          px: 2,
          py: 2,
          bgcolor: "rgba(255,255,255,0.88)",
          backdropFilter: "blur(14px)",
          borderTop: "1px solid rgba(0,0,0,0.06)",
          boxShadow: "0 -2px 12px rgba(0,0,0,0.04)",
        }}
      >
        <Button
          fullWidth
          variant="contained"
          disabled={!isFormValid}
          onClick={handleAddMembers}
          startIcon={<PersonAddIcon />}
          sx={{
            borderRadius: BORDER_RADIUS_BUTTON,
            minHeight: 52,
            fontWeight: 700,
            fontSize: "1rem",
            textTransform: "none",
            bgcolor: "#FF9015",
            color: "#fff",
            boxShadow: "0 4px 14px rgba(255,144,21,0.35)",
            "&:hover": {
              bgcolor: "#e8820f",
              boxShadow: "0 6px 20px rgba(255,144,21,0.45)",
            },
            "&:active": {
              transform: "scale(0.97)",
            },
            "&.Mui-disabled": {
              bgcolor: "rgba(255,144,21,0.35)",
              color: "rgba(255,255,255,0.7)",
            },
            transition: TRANSITION_SMOOTH,
          }}
        >
          <Langs str="ADD" />
        </Button>
      </Box>
    </Dialog>
  );
}
